// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Checkbox, Col, Input, InputNumber, Modal, Radio, Row, Select, Steps, Switch, Tabs} from "antd";
import * as SyncerBackend from "../../services/SyncerBackend";
import * as OrganizationBackend from "../../services/OrganizationBackend";
import * as Setting from "../../conf/Setting";
import i18next from "i18next";
import SyncerTableColumnTable from "../../components/syncer/SyncerTableColumnTable";
import moment from "moment";
import {v4 as uuidv4} from "uuid";
import UpstreamSyncerLogPage from "./UpstreamSyncerLogPage";

// import {Controlled as CodeMirror} from "react-codemirror2";
import "codemirror/lib/codemirror.css";
require("codemirror/theme/material-darker.css");
require("codemirror/mode/javascript/javascript");

const {TabPane} = Tabs;
const {Option} = Select;
const {Step} = Steps;

const syncerTypeMap = {
  0: "user",
  1: "department",
  2: "relationship",
};

const syncerStrategyUserOptions = [
  {value: 0, label: i18next.t("syncer:Create user")},
  {value: 1, label: i18next.t("syncer:Update user")},
  {value: 2, label: i18next.t("syncer:Delete user")},
];

const syncerStrategyDepartmentOptions = [
  {value: 0, label: i18next.t("syncer:Create department")},
  {value: 1, label: i18next.t("syncer:Update department")},
  {value: 2, label: i18next.t("syncer:Delete department")},
];

class UpstreamSyncerEditPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      syncerId: props.match.params.syncerId,
      owner: props.match.params.owner,
      syncer: null,
      organizations: [],
      mode: props.mode === "add" ? "add" : "edit",
      defaultActiveKey: props.location.tabMode !== undefined && props.location.tabMode === "log" ? "syncLog" : "syncConfig",
      tabMode: props.location.tabMode !== undefined ? props.location.tabMode : "syncer",
      current: 0,
      localNames: [],
      testLoading: false,
      synceLoading: false,
      saveLoading: false,
      activeKey: "",
      syncTipsVisible: false,
    };
  }

  newSyncer() {
    const randomName = Setting.getRandomName();
    return {
      owner: "",  // 组织uid
      uid: uuidv4(),
      displayName: `syncer_${randomName}`,  // 名称
      syncType: 0, // 同步类型
      syncerStrategy: [0, 1], // 同步策略
      host: "localhost", // 数据库host
      port: 3306, // 数据库port
      user: "", // 数据库登录账号user
      password: "", // 数据库登录账号password
      databaseType: 0, // 数据库类型
      database: "", // 数据库库名
      table: "", // 数据库表名
      tableColumns: [], // 表字段对应关系
      syncInterval: 1, // 同步时间间隔 天为单位
      isEnabled: true, // 启用禁用
      isAutoUpdate: false, // 是否是自动更新， true自动更新
      isCoverage: false, // 是否覆盖本地数据，该参数仅在同步用户部门关系时使用
      databaseSchema: "", // 数据库模式，当选择PostgreSQL时编辑

      createdTime: moment().format("yyyy-MM-DD HH:mm:ss"),
      updatedTime: moment().format("yyyy-MM-DD HH:mm:ss"),
    };
  }

  UNSAFE_componentWillMount() {
    if (this.state.mode === "add") {
      const syncer = this.newSyncer();
      this.setState({syncer: syncer});
      this.getLocalName(syncerTypeMap[syncer.syncType]);
    } else {
      this.getSyncer(this.state.syncerId, this.state.owner);
    }

    this.getOrganizations();
  }

  getSyncer(syncerId, owner) {
    SyncerBackend.getUpstreamSyncerById(syncerId, owner)
      .then((res) => {
        if (res.msg === undefined) {
          const tableColumns = res.tableColumns.map(item => {
            if (res.syncType === 0 && item.localName === "Name") {
              item.isPermanent = true;
            }
            if (res.syncType === 1 && item.localName === "Code") {
              item.isPermanent = true;
            }
            if (res.syncType === 2 && ["Name", "Code"].includes(item.localName)) {
              item.isPermanent = true;
            }
            return item;
          });
          res.tableColumns = tableColumns;
          this.setState({
            syncer: res,
          });
          this.getLocalName(syncerTypeMap[res.syncType]);
        }
      });
  }

  getLocalName(type, operChange = false) {
    SyncerBackend.getLocalName(type)
      .then((res) => {
        if (res.msg === undefined) {
          const localNames = Object.keys(res).map(key => ({
            id: key,
            value: res[key],
          }));
          if (this.state.mode === "add" || operChange) {
            const tableColumns = Setting.getUpstreamSyncerTableColumns(type);
            const syncer = this.state.syncer;
            syncer.tableColumns = tableColumns;
            this.setState({
              localNames: localNames,
              syncer: syncer,
            });
          } else {
            this.setState({
              localNames: localNames,
            });
          }
        }
      });
  }

  getOrganizations() {
    OrganizationBackend.getOrganizations(Setting.Admin)
      .then((res) => {
        if (res.msg === undefined) {
          const org = res.filter(organization => !Setting.isAdminOrganization(organization.uid)).reverse();
          this.setState({
            organizations: org,
          });
          if (this.state.mode === "add") {
            this.updateSyncerField("owner", org.length > 0 ? org[0].uid : "");
          }
        } else {
          this.setState({
            organizations: [],
          });
        }
      });
  }

  parseSyncerField(key, value) {
    if (["port"].includes(key)) {
      value = Setting.myParseInt(value);
    }
    return value;
  }

  updateSyncerField(key, value) {
    value = this.parseSyncerField(key, value);

    const syncer = this.state.syncer;
    syncer[key] = value;
    this.setState({
      syncer: syncer,
    });
  }

  renderDatabaseConfig() {
    return (
      <div hidden={this.state.current !== 0}>
        <Row style={{marginTop: "10px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("general:Organization"), i18next.t("general:Organization - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Select virtual={false} style={{width: "100%"}} value={this.state.syncer.owner} onChange={(value => {this.updateSyncerField("owner", value);})}>
              {
                this.state.organizations.map((organization, index) => <Option key={index} value={organization.uid}>{organization.displayName}</Option>)
              }
            </Select>
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("general:Name"), i18next.t("general:Name - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input value={this.state.syncer.displayName} maxLength={50} showCount onChange={e => {
              this.updateSyncerField("displayName", e.target.value);
            }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Database type"), i18next.t("syncer:Database type - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Select virtual={false} style={{width: "100%"}} value={this.state.syncer.databaseType} onChange={(value => {this.updateSyncerField("databaseType", value);})}>
              {
                [
                  {id: 0, name: "PostgreSQL"},
                  {id: 1, name: "MySQL"},
                ].map((databaseType, index) => <Option key={index} value={databaseType.id}>{databaseType.name}</Option>)
              }
            </Select>
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Host"), i18next.t("syncer:Host - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input value={this.state.syncer.host} maxLength={200} showCount onChange={e => {
              this.updateSyncerField("host", e.target.value);
            }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Port"), i18next.t("syncer:Port - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <InputNumber value={this.state.syncer.port} min={1} max={65535} onChange={value => {
              this.updateSyncerField("port", value);
            }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Username"), i18next.t("syncer:Username - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input value={this.state.syncer.user} maxLength={100} showCount onChange={e => {
              this.updateSyncerField("user", e.target.value);
            }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("general:Password"), i18next.t("general:Password - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input.Password value={this.state.syncer.password} maxLength={100} showCount onChange={e => {
              this.updateSyncerField("password", e.target.value);
            }} />
          </Col>
        </Row>
        {
          this.state.syncer.databaseType === 0 ?
            <Row style={{marginTop: "20px"}} >
              <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                {Setting.getLabel(i18next.t("syncer:Database schema"), i18next.t("syncer:Database schema"), true)} :
              </Col>
              <Col span={22} >
                <Input value={this.state.syncer.databaseSchema} maxLength={200} showCount onChange={e => {
                  this.updateSyncerField("databaseSchema", e.target.value);
                }} />
              </Col>
            </Row>
            :
            null
        }
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Databse name"), i18next.t("syncer:Databse name - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input value={this.state.syncer.database} maxLength={200} showCount onChange={e => {
              this.updateSyncerField("database", e.target.value);
            }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("syncer:Table name"), i18next.t("syncer:Table name - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input value={this.state.syncer.table} maxLength={200} showCount
              disabled={this.state.syncer.type === "Keycloak"} onChange={e => {
                this.updateSyncerField("table", e.target.value);
              }} />
          </Col>
        </Row>
      </div>
    );
  }

  renderTaskConfig() {
    return <div hidden={this.state.current !== 1}>
      <Row style={{marginTop: "20px"}} >
        <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
          {Setting.getLabel(i18next.t("syncer:Sync type"), i18next.t("syncer:Sync type - Tooltip"))} :
        </Col>
        <Col span={22} >
          <Select virtual={false} style={{width: "100%"}} value={this.state.syncer.syncType} onChange={(value => {
            this.updateSyncerField("syncType", value);
            this.getLocalName(syncerTypeMap[value], true);
            this.updateSyncerField("isCoverage", false);
            if (value === 2) {
              this.updateSyncerField("syncerStrategy", [1]);
            } else {
              this.updateSyncerField("syncerStrategy", [0, 1]);
            }
          })}>
            {
              Setting.upstreamSyncTypes.map((syncType, index) => <Option key={index} value={syncType.id}>{syncType.name}</Option>)
            }
          </Select>
          <a style={{marginTop: 6}} onClick={() => {this.setState({syncTipsVisible: true});}}>{i18next.t("syncer:Sync rules")}</a>
        </Col>
      </Row>
      {
        this.state.syncer.syncType !== 2 ?
          <Row style={{marginTop: "20px"}} >
            <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
              {Setting.getLabel(i18next.t("syncer:Match rule"), i18next.t("syncer:Match rule - Tooltip"))} :
            </Col>
            <Col span={22} >
              <Checkbox.Group options={this.state.syncer.syncType === 1 ? syncerStrategyDepartmentOptions : syncerStrategyUserOptions}
                value={this.state.syncer.syncerStrategy} onChange={checkedValue => {
                  this.updateSyncerField("syncerStrategy", checkedValue);
                }} />
            </Col>
          </Row>
          :
          <Row style={{marginTop: "20px"}} >
            <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
              {Setting.getLabel(i18next.t("syncer:Covered local"), i18next.t("syncer:Covered local - Tooltip"))} :
            </Col>
            <Col span={22} >
              <Switch checked={this.state.syncer.isCoverage} onChange={checked => {
                this.updateSyncerField("isCoverage", checked);
              }} />
            </Col>
          </Row>
      }
      <Row style={{marginTop: "20px"}} >
        <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
          {Setting.getLabel(i18next.t("syncer:Field mapping"), i18next.t("syncer:Field mapping - Tooltip"))} :
        </Col>
        <Col span={22} >
          <SyncerTableColumnTable
            title={i18next.t("syncer:Field mapping")}
            table={this.state.syncer.tableColumns}
            localNames={this.state.localNames}
            onUpdateTable={(value) => {this.updateSyncerField("tableColumns", value);}}
            syncType={this.state.syncer.syncType}
            onUpdateIsCovered={(isCovered) => {
              this.updateSyncerField("isCoverage", isCovered);
            }}
          />
        </Col>
      </Row>
      <Row style={{marginTop: "20px"}} >
        <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
          {Setting.getLabel(i18next.t("syncer:Sync mode"), i18next.t("syncer:Sync mode - Tooltip"))} :
        </Col>
        <Col span={22} >
          <div>
            <Radio.Group onChange={(e) => {
              this.updateSyncerField("isAutoUpdate", e.target.value);
            }} value={this.state.syncer.isAutoUpdate}>
              <Radio value={false}>{i18next.t("syncer:Sync manually")}</Radio>
              <Radio value={true}>{i18next.t("syncer:Sync automatically")}</Radio>
            </Radio.Group>
          </div>
          {
            this.state.syncer.isAutoUpdate ?
              <>
                <InputNumber style={{marginTop: 20}} value={this.state.syncer.syncInterval} onChange={value => {
                  this.updateSyncerField("syncInterval", value);
                }} />&nbsp;&nbsp;{i18next.t("syncer:Day")}
                <div style={{marginTop: 10}}>{i18next.t("syncer:Syncer task running at 7")}</div>
              </>
              :
              <Button style={{marginTop: 20}} hidden={this.state.syncerId === undefined} type="default" loading={this.state.synceLoading}
                onClick={() => this.doSync()}>{i18next.t("syncer:Sync")}</Button>
          }
        </Col>
      </Row>
    </div>;
  }

  renderSyncer() {
    return <div>
      <Row>
        <Col span={24}>
          <Steps
            current={this.state.current}
            style={{
              width: "90%",
              maxWidth: "500px",
              margin: "auto",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <Step
              title={i18next.t("syncer:Databse config")}
            />
            <Step
              title={i18next.t("syncer:Task config")}
            />
          </Steps>
        </Col>
      </Row>
      {
        this.state.syncer ?
          this.renderDatabaseConfig()
          :
          null
      }
      {
        this.state.syncer ?
          this.renderTaskConfig()
          :
          null
      }
      <div style={{marginTop: "20px", marginLeft: "40px", textAlign: "right"}}>
        <Button hidden={this.state.current === 1} style={{marginLeft: "20px"}} onClick={() => this.exit()}>{i18next.t("general:Cancel")}</Button>
        <Button hidden={this.state.current === 1} style={{marginLeft: "20px"}} loading={this.state.testLoading} onClick={() => this.testSync()}>{i18next.t("syncer:Test connection")}</Button>
        <Button hidden={this.state.current === 1} style={{marginLeft: "20px"}} type="primary" onClick={() => {this.next();}}>{i18next.t("syncer:Next")}</Button>
        <Button hidden={this.state.current === 0} style={{marginLeft: "20px"}} onClick={() => {this.setState({current: 0});}}>{i18next.t("syncer:Previous")}</Button>
        <Button hidden={this.state.current === 0} style={{marginLeft: "20px"}} loading={this.state.saveLoading} onClick={() => this.submitSyncerEdit(false)}>{i18next.t("general:Save")}</Button>
        <Button hidden={this.state.current === 0} style={{marginLeft: "20px"}} loading={this.state.saveLoading} type="primary" onClick={() => this.submitSyncerEdit(true)}>{i18next.t("general:Save & Exit")}</Button>
      </div>
    </div>;
  }

  validDatabaseConfig() {
    const syncer = Setting.deepCopy(this.state.syncer);
    if (syncer.displayName === "") {
      Setting.showMessage("error", i18next.t("syncer:Displayname required"));
      return false;
    }

    if (syncer.host === "") {
      Setting.showMessage("error", i18next.t("syncer:Host required"));
      return false;
    }

    if (syncer.port === "") {
      Setting.showMessage("error", i18next.t("syncer:Port required"));
      return false;
    }

    if (syncer.user === "") {
      Setting.showMessage("error", i18next.t("syncer:User required"));
      return false;
    }

    if (syncer.password === "") {
      Setting.showMessage("error", i18next.t("syncer:Password required"));
      return false;
    }

    if (syncer.databaseType === 0 && syncer.databaseSchema === "") {
      Setting.showMessage("error", i18next.t("syncer:DatabaseSchema required"));
      return false;
    }

    if (syncer.database === "") {
      Setting.showMessage("error", i18next.t("syncer:Database required"));
      return false;
    }

    if (syncer.table === "") {
      Setting.showMessage("error", i18next.t("syncer:Table required"));
      return false;
    }

    return true;
  }
  next() {
    if (this.validDatabaseConfig()) {
      this.setState({current: 1});
    }
  }

  submitSyncerEdit(willExit) {
    const syncer = Setting.deepCopy(this.state.syncer);

    const foundIndex = syncer.tableColumns.findIndex(item => {
      if (item.name.trim() === "") {
        return true;
      }
      return false;
    });

    if (foundIndex !== -1) {
      Setting.showMessage("error", i18next.t("syncer:Table field required"));
      return;
    }

    this.setState({saveLoading: true});
    if (this.state.mode === "add") {
      SyncerBackend.addUpstreamSyncer(syncer)
        .then((res) => {
          if (res.msg === "") {
            Setting.showMessage("success", i18next.t("general:Successfully saved"));

            if (willExit) {
              this.exit();
            } else {
              this.setState({
                syncerId: syncer.uid,
                owner: syncer.owner,
                mode: "edit",
              });
              this.props.history.replace(`/syncers/upstream/${syncer.owner}/${syncer.uid}`);
              this.getSyncer(syncer.uid, syncer.owner);
            }
          } else {
            Setting.showResponseMessage(res.msg);
          }
          this.setState({saveLoading: false});
        })
        .catch(error => {
          Setting.showResponseMessage(error);
          this.setState({saveLoading: false});
        });
    } else {
      SyncerBackend.updateUpstreamSyncer(this.state.syncerId, this.state.owner, syncer)
        .then((res) => {
          if (res.msg === "") {
            Setting.showMessage("success", i18next.t("general:Successfully saved"));

            if (willExit) {
              this.exit();
            } else {
              this.setState({
                owner: syncer.owner,
              });
              this.props.history.replace(`/syncers/upstream/${syncer.owner}/${syncer.uid}`);
            }
          } else {
            Setting.showResponseMessage(res.msg);
          }
          this.setState({saveLoading: false});
        })
        .catch(error => {
          Setting.showResponseMessage(error);
          this.setState({saveLoading: false});
        });
    }
  }

  deleteSyncer() {
    SyncerBackend.deleteSyncer(this.state.syncer)
      .then(() => {
        this.exit();
      })
      .catch(error => {
        Setting.showResponseMessage(error);
      });
  }

  testSync() {
    if (!this.validDatabaseConfig()) {
      return;
    }
    const syncer = Setting.deepCopy(this.state.syncer);
    this.setState({testLoading: true});
    SyncerBackend.testUpstreamSync(syncer)
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("syncer:Connected successfully"));
        } else {
          Setting.showResponseMessage(res.msg);
        }
        this.setState({testLoading: false});
      })
      .catch(error => {
        if (error?.name === "AbortError") {
          Setting.showMessage("error", i18next.t("syncer:Connected timeout"));
        } else {
          Setting.showResponseMessage(error.message);
        }
        this.setState({testLoading: false});
      });
  }

  doSync() {
    this.setState({synceLoading: true});
    SyncerBackend.doUpstreamSync(this.state.syncerId, this.state.owner)
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("syncer:Sync successfully"));
        } else {
          Setting.showResponseMessage(res.msg);
        }
        this.setState({synceLoading: false});
      })
      .catch(error => {
        Setting.showResponseMessage(error);
        this.setState({synceLoading: false});
      });
  }

  exit() {
    this.props.history.push("/syncers");
  }

  render() {
    return (
      <>
        {
          this.state.mode === "add" ?
            this.renderSyncer()
            :
            <Tabs
              defaultActiveKey={this.state.defaultActiveKey}
              onChange={(activeKey) => {
                this.setState({activeKey: activeKey});
              }}
            >
              <TabPane tab={i18next.t("syncer:Syncer config")} key="syncConfig">
                {this.renderSyncer()}
              </TabPane>
              <TabPane tab={i18next.t("syncer:Syncer logs")} key="syncLog">
                {
                  this.state.syncer ?
                    <UpstreamSyncerLogPage visible={this.state.activeKey === "syncLog"} syncerId={this.state.syncer.uid} {...this.props} />
                    :
                    null
                }
              </TabPane>
            </Tabs>
        }
        <Modal
          title={i18next.t("syncer:Sync rules")}
          visible={this.state.syncTipsVisible}
          closable={false}
          footer={[
            <Button key="showTips" type="primary" onClick={() => {this.setState({syncTipsVisible: false});}}>
              {i18next.t("syncer:Sync rules confirm")}
            </Button>,
          ]}
        >
          <div style={{height: "calc(100vh - 300px)", overflow: "auto"}}>
          一、用户同步<br />
          1.匹配用户名字段，如果上游没有用户A，统一身份认证有用户A，如果勾选“删除用户”则删除该用户，如果未勾选“删除用户”保留该用户； <br />
          2.匹配用户名字段，如果上游有用户A，统一身份认证没有用户A，如果勾选“创建用户”则新增该用户，如果未勾选“创建用户”，则不新增该用户；<br />
          3.匹配用户名字段，如果上游有用户A，统一身份认证也有用户A，如果勾选“更新用户”则更新该用户，如果未勾选“更新用户”则不更新该用户；<br />
          4.如果与同一组织内身份证号重复，则新用户身份证号为空；<br />
          5.如果与同一组织内手机号重复，则新用户手机号为空；<br />
          6.用户账号默认密码为IAM@123#<br />
            <br />
          二、部门同步<br />
          1.匹配部门code字段，如果上游没有部门A，统一身份认证有部门A，如果勾选“删除部门”则删除该部门，如果未勾选“删除部门”保留该部门；<br />
          2.匹配部门code字段，如果上游有部门A，统一身份认证没有部门A，如果勾选“创建部门”则新增该部门，如果未勾选“创建部门”，则不新增该部门；<br />
          3.匹配部门code字段，如果上游有部门A，统一身份认证也有部门A，如果勾选“更新部门”则更新该部门（父节点code不一致，以上游为准调整到新部门），如果未勾选“更新部门”则不更新该部门；<br />
          4.根据父节点code同步到对应部门或组织下，如果没有父节点code，该部门属于组织根目录下；<br />
            <br />
          三、用户部门关系同步<br />
          1.是否覆盖本地：在本地用户A属于部门A和部门B，在上游用户A属于部门C；开启了“覆盖本地”开关，用户A属于部门C；如果关闭了选项，用户A属于部门A、B、C，id字段只能开启，其他默认选择关闭；<br />
          2.如果匹配到相同的部门code，用户关联到该部门下；<br />
          3.如果匹配不到任何部门code，则将用户存在组织根目录下。<br />
            <br />
          四、采用全量更新方式<br />
            <br />
          五、每次同步会备份一次数据库，没有界面维护，超过180天备份数据自动删除；<br />
          </div>
        </Modal>
      </>

    );
  }
}

export default UpstreamSyncerEditPage;
